import { useEffect, useState } from "react";
import { api, brl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Target, Sparkles } from "lucide-react";

// Compact goal widget — used on Dashboard and POS/Tabs. Auto-refreshes every 60s.
export default function DailyGoalWidget({ compact = false }) {
  const [g, setG] = useState(null);

  const load = () => api.get("/goals/today").then(r=>setG(r.data)).catch(()=>{});
  useEffect(() => {
    load();
    const i = setInterval(load, 60000);
    const onSale = () => load();
    window.addEventListener("sale-completed", onSale);
    return () => { clearInterval(i); window.removeEventListener("sale-completed", onSale); };
  }, []);

  if (!g) return null;
  if (!g.is_operating_day) {
    return compact ? null : (
      <Card className="border-neutral-200 bg-neutral-50"><CardContent className="p-4 text-center text-sm text-muted-foreground" data-testid="daily-goal-widget">
        📅 Hoje não é dia de operação, sem meta
      </CardContent></Card>
    );
  }

  const colorMap = { red:"bg-rose-500", yellow:"bg-amber-500", green:"bg-emerald-500" };
  const textMap  = { red:"text-rose-700", yellow:"text-amber-700", green:"text-emerald-700" };
  const pct = Math.min(100, g.percent);
  const hit = g.percent >= 100;

  if (compact) {
    return (
      <div className="rounded-full bg-white border shadow-sm px-3 py-1.5 flex items-center gap-2 text-xs font-medium" data-testid="daily-goal-widget-compact">
        <Target className={`w-3.5 h-3.5 ${textMap[g.status]}`}/>
        <span>Meta hoje <b>{brl(g.target)}</b></span>
        <span className="text-muted-foreground">•</span>
        <span className={textMap[g.status]}>{hit ? "✓ batida" : `falta ${brl(g.remaining)}`}</span>
        <div className="w-20 h-1.5 bg-neutral-100 rounded-full overflow-hidden ml-1">
          <div className={`h-full ${colorMap[g.status]} transition-all`} style={{width: `${pct}%`}}/>
        </div>
      </div>
    );
  }

  return (
    <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50/60 to-transparent" data-testid="daily-goal-widget">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-emerald-600 text-white grid place-items-center"><Target className="w-5 h-5"/></div>
            <div>
              <div className="font-semibold text-sm">Meta de hoje {g.source === "auto" && <span className="text-[10px] font-normal text-muted-foreground">(sugerida)</span>}</div>
              <div className="text-xs text-muted-foreground">{new Date(g.date+"T12:00").toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "short" })}</div>
              {g.mode === "beyond_goal" && (
                <div className="text-[10px] text-emerald-700 mt-0.5" data-testid="daily-goal-beyond-goal">
                  Meta do mês batida. Sugestão extra pelo seu histórico
                </div>
              )}
              {g.mode === "performance" && (
                <div className="text-[10px] text-muted-foreground mt-0.5" data-testid="daily-goal-by-performance">
                  Pelo seu faturamento real das últimas semanas
                </div>
              )}
            </div>
          </div>
          {hit ? (
            <div className="flex items-center gap-1 text-emerald-700 font-semibold text-sm" data-testid="goal-hit-badge"><Sparkles className="w-4 h-4"/>Meta batida!</div>
          ) : (
            <div className="text-xs text-muted-foreground">Faltam <b className={textMap[g.status]}>{brl(g.remaining)}</b></div>
          )}
        </div>
        <div className="flex items-baseline justify-between mb-1">
          <span className="text-2xl md:text-3xl font-bold tracking-tight">{brl(g.achieved)}</span>
          <span className="text-sm text-muted-foreground">de {brl(g.target)}</span>
        </div>
        <div className="h-3 bg-neutral-100 rounded-full overflow-hidden">
          <div className={`h-full ${colorMap[g.status]} transition-all`} style={{width: `${pct}%`}}/>
        </div>
        <div className="text-xs mt-1.5 text-right font-medium">{g.percent}%</div>
        {hit && (
          <div className="text-[11px] text-emerald-700 mt-1.5" data-testid="daily-goal-hit-note">
            🎉 Meta de hoje batida. Agora vale vender com foco em margem, além do volume.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
